<!-- ── Demo-Organisationen (#933) ──────────────────────────────────────────────────────────────────────────
     The shared fiskaly TEST organisations demo locations are placed under. The chip reports free slots, the
     number that decides whether the next „fiskaly registrieren" on a demo location works. -->
<section aria-labelledby="demo-organizations-title"
         class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex min-w-0 items-center gap-3">
      <span aria-hidden="true"
            class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gray-50 text-sm font-bold
                   text-theme-primary-700">
        DE
      </span>
      <div class="min-w-0">
        <h2 class="text-base font-bold tracking-tight text-gray-800" id="demo-organizations-title">
          Demo-Organisationen
        </h2>
        <p class="mt-0.5 text-xs text-gray-500">Fiskaly-Testorganisationen für Demo-Mandanten</p>
      </div>
    </div>
    <span [class]="chipClass(chip())" data-testid="demo-organizations-chip"
          class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold">
      {{ chip().label }}
    </span>
  </div>

  @if (organizations(); as pool) {
    @if (pool.length > 0) {
      <ul class="mt-4 space-y-2" data-testid="demo-organizations-list">
        @for (organization of pool; track organization.id) {
          <li class="flex flex-col gap-2 rounded-xl bg-gray-50 px-3 py-2 sm:flex-row sm:items-center
                     sm:justify-between">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-gray-800">{{ organization.label }}</p>
              <p class="break-all font-mono text-xs text-gray-500">{{ organization.organizationId }}</p>
            </div>
            <div class="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
              <span class="text-xs text-gray-600" data-testid="demo-organization-usage">
                {{ organization.used }} von {{ organization.capacity }} belegt
              </span>
              <!-- Disabled while a demo sits in the organisation: the server refuses that removal anyway, and a
                   button that can only fail is noise. The title says why. -->
              <button (click)="askRemove(organization)" [disabled]="organization.used > 0"
                      [attr.aria-label]="organization.label + ' entfernen'"
                      [attr.title]="organization.used > 0 ? 'Enthält noch Demo-Standorte' : null"
                      class="rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors
                             duration-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-30
                             disabled:grayscale"
                      type="button">
                Entfernen
              </button>
            </div>
          </li>
        }
      </ul>
    } @else {
      <p class="mt-4 text-sm text-gray-600" data-testid="demo-organizations-empty">
        Noch keine Demo-Organisation eingerichtet. Ohne sie lässt sich kein Standort eines Demo-Mandanten bei
        Fiskaly registrieren.
      </p>
    }
  }

  <div class="mt-4 border-t border-gray-100 pt-3">
    <button (click)="toggleForm()" [attr.aria-expanded]="formOpen()" aria-controls="demo-organization-form"
            class="group -mx-2 flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl px-2
                   py-1.5 text-left transition-colors duration-200 hover:bg-gray-50 focus-visible:outline
                   focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500"
            type="button">
      <span class="text-sm font-bold tracking-tight text-gray-700">Demo-Organisation hinzufügen</span>
      <span class="inline-flex shrink-0 items-center gap-1.5 rounded-lg border-[1px] border-gray-200 bg-white
                   px-2.5 py-1 text-sm font-medium text-gray-600 shadow-sm transition-colors duration-200
                   group-hover:border-gray-300 group-hover:bg-gray-100 group-hover:text-gray-900">
        <span>{{ formOpen() ? 'Ausblenden' : 'Anzeigen' }}</span>
        <svg [class.rotate-180]="formOpen()" aria-hidden="true"
             class="h-4 w-4 shrink-0 origin-center transition-transform duration-200" fill="none"
             stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
        </svg>
      </span>
    </button>

    <!-- Rendered always, the @if inside: `aria-controls` above must resolve while collapsed. -->
    <div id="demo-organization-form">
      @if (formOpen()) {
        <p class="mt-2 text-xs text-gray-500">
          Legen Sie die Organisation im Fiskaly-Dashboard in der Testansicht an und erstellen Sie dort einen
          API-Key. Schalten Sie für diese Organisation nie die Live-Umgebung frei. Beim Hinzufügen wird geprüft, ob
          der Schlüssel zur Testumgebung und zu dieser Organisation gehört. Eine Organisation fasst fünf TSS.
        </p>
        <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="demo-organization-id">
              Organisations-ID
            </label>
            <input (input)="organizationId.set(value($event))" [value]="organizationId()"
                   class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                          shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                          focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                   autocomplete="off" id="demo-organization-id" name="demo-organization-id"
                   placeholder="z. B. 8b2c…" type="text">
          </div>
          <div>
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="demo-organization-label">
              Bezeichnung (optional)
            </label>
            <input (input)="label.set(value($event))" [value]="label()"
                   class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                          shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                          focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                   autocomplete="off" id="demo-organization-label" name="demo-organization-label"
                   placeholder="z. B. Demo 2" type="text">
          </div>
          <div>
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="demo-organization-key">
              API-Key (Test)
            </label>
            <input (input)="apiKey.set(value($event))" [value]="apiKey()"
                   class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                          shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                          focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                   autocomplete="off" id="demo-organization-key" name="demo-organization-key" type="text">
          </div>
          <div>
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="demo-organization-secret">
              API-Secret
            </label>
            <input (input)="apiSecret.set(value($event))" [value]="apiSecret()"
                   class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                          shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                          focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                   autocomplete="new-password" id="demo-organization-secret" name="demo-organization-secret"
                   type="password">
          </div>
        </div>
        <div class="mt-3 flex justify-end">
          <button (click)="add()" [disabled]="saving()"
                  class="inline-flex w-full shrink-0 items-center justify-center rounded-2xl bg-theme-primary-600
                         px-5 py-2.5 font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all
                         duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                         disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale sm:w-auto sm:text-sm"
                  type="button">
            Hinzufügen
          </button>
        </div>
      }
    </div>
  </div>
</section>

@if (pendingRemove(); as organization) {
  <!-- Backdrop click and Escape cancel: removing is only our record, and cancelling leaves everything as it
       was, so an accidental exit costs nothing (style guide, destructive-confirmation dialog). -->
  <div (click)="cancelRemove()" (keydown.escape)="cancelRemove()"
       class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
    <div (click)="$event.stopPropagation()" aria-describedby="demo-organization-remove-body"
         aria-labelledby="demo-organization-remove-heading" aria-modal="true"
         class="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl"
         role="alertdialog">
      <div class="space-y-4 p-4 sm:p-6">
        <h2 class="text-xl font-bold tracking-tight text-gray-900" id="demo-organization-remove-heading">
          „{{ organization.label }}" entfernen?
        </h2>
        <div class="space-y-3 text-sm text-gray-700" id="demo-organization-remove-body">
          <p>
            Organisations-ID:
            <span class="break-all font-mono text-xs text-gray-500">{{ organization.organizationId }}</span>
          </p>
          <p>
            Die Organisation wird nur aus dieser Liste entfernt. Bei Fiskaly bleibt sie bestehen, und neue Demos
            werden nicht mehr in ihr registriert.
          </p>
        </div>
        <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <button #cancelButton (click)="cancelRemove()"
                  class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4 py-2.5
                         text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                         hover:border-gray-300 hover:bg-gray-50 active:scale-95"
                  type="button">
            Abbrechen
          </button>
          <button (click)="confirmRemove()" [disabled]="removing()"
                  class="inline-flex items-center justify-center rounded-2xl bg-red-600 px-5 py-2.5 text-sm
                         font-semibold text-white shadow-lg shadow-red-100 transition-all duration-200
                         hover:bg-red-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed
                         disabled:opacity-30 disabled:grayscale"
                  type="button">
            Ja, entfernen
          </button>
        </div>
      </div>
    </div>
  </div>
}
